import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Launch as LaunchIcon,
  GitHub as GitHubIcon,
  Flag as FlagIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  ImageNotSupported as NoImageIcon,
} from '@mui/icons-material';
import VoteButton from './VoteButton';
import ReportDialog from './ReportDialog';
import {
  fetchGalleryItem,
  deleteGalleryItem,
  GalleryItem,
} from '../api/galleryApi';
import useAuth from '../../../features/auth/hooks/useAuth';
import brand from '../../../config/brand';

const GalleryItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user } = useAuth();

  const [item, setItem] = useState<GalleryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeMedia, setActiveMedia] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwner = user?.id === item?.authorId;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchGalleryItem(id)
      .then((data) => {
        setItem(data);
        setActiveMedia(0);
      })
      .catch(() => navigate('/gallery'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!item || !window.confirm('Are you sure you want to delete this project?'))
      return;
    setDeleting(true);
    try {
      await deleteGalleryItem(item._id);
      navigate('/gallery');
    } catch {
      setDeleting(false);
    }
  };

  const navigateMedia = (dir: 'prev' | 'next') => {
    if (!item) return;
    const len = item.media.length;
    setActiveMedia((prev) =>
      dir === 'prev' ? (prev - 1 + len) % len : (prev + 1) % len
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Skeleton variant="text" height={50} />
            <Skeleton variant="text" height={30} width="60%" />
            <Skeleton variant="rounded" height={200} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!item) return null;

  const currentMedia = item.media?.[activeMedia];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      {/* Back link */}
      <Button
        component={RouterLink}
        to="/gallery"
        startIcon={<BackIcon />}
        sx={{
          mb: 3,
          color: 'text.secondary',
          fontWeight: 600,
          '&:hover': { color: brand.colors.fire },
        }}
      >
        Back to Gallery
      </Button>

      <Grid container spacing={4}>
        {/* Media section */}
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.08)',
              bgcolor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.02)'
                  : 'rgba(0,0,0,0.02)',
            }}
          >
            {item.media?.length > 0 ? (
              <>
                {currentMedia?.type === 'video' ? (
                  <Box
                    component="video"
                    controls
                    src={currentMedia.url}
                    sx={{
                      width: '100%',
                      height: { xs: 280, md: 450 },
                      objectFit: 'contain',
                      bgcolor: 'black',
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={currentMedia?.url}
                    alt={item.title}
                    sx={{
                      width: '100%',
                      height: { xs: 280, md: 450 },
                      objectFit: 'contain',
                    }}
                  />
                )}

                {/* Navigation arrows */}
                {item.media.length > 1 && (
                  <>
                    <IconButton
                      onClick={() => navigateMedia('prev')}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      }}
                    >
                      <PrevIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => navigateMedia('next')}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      }}
                    >
                      <NextIcon />
                    </IconButton>

                    {/* Dots */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 0.8,
                      }}
                    >
                      {item.media.map((_, i) => (
                        <Box
                          key={i}
                          onClick={() => setActiveMedia(i)}
                          sx={{
                            width: i === activeMedia ? 24 : 8,
                            height: 8,
                            borderRadius: 4,
                            bgcolor:
                              i === activeMedia
                                ? brand.colors.fire
                                : 'rgba(255,255,255,0.5)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                          }}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Box
                sx={{
                  height: { xs: 280, md: 450 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <NoImageIcon
                  sx={{ fontSize: 80, color: 'text.disabled' }}
                />
              </Box>
            )}
          </Box>

          {/* Thumbnail strip */}
          {item.media?.length > 1 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 2,
                overflowX: 'auto',
                pb: 1,
              }}
            >
              {item.media.map((m, i) => (
                <Box
                  key={m._id}
                  onClick={() => setActiveMedia(i)}
                  sx={{
                    width: 72,
                    height: 54,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor:
                      i === activeMedia
                        ? brand.colors.fire
                        : 'transparent',
                    opacity: i === activeMedia ? 1 : 0.6,
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={m.thumbnailUrl || m.url}
                    alt=""
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        {/* Info section */}
        <Grid item xs={12} md={5}>
          <Box>
            {/* Title */}
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ mb: 1, fontSize: { xs: '1.75rem', md: '2.25rem' } }}
            >
              {item.title}
            </Typography>

            {/* Author & Category */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 3,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                by <strong>{item.authorName}</strong>
              </Typography>
              {item.category && (
                <Chip
                  label={item.category}
                  size="small"
                  sx={{
                    bgcolor: `rgba(${brand.colorsRgb.fire}, 0.12)`,
                    color: brand.colors.fire,
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {/* Stats row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                mb: 3,
                pb: 3,
                borderBottom: '1px solid',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.08)',
              }}
            >
              <VoteButton
                itemId={item._id}
                initialCount={item.voteCount}
                initialVoted={item.hasVoted || false}
                disabled={isOwner}
                size="large"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ViewIcon sx={{ color: 'text.disabled' }} />
                <Typography color="text.secondary" fontWeight={600}>
                  {item.viewCount || 0} views
                </Typography>
              </Box>
            </Box>

            {/* Description */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                whiteSpace: 'pre-wrap',
              }}
            >
              {item.description}
            </Typography>

            {/* Tags */}
            {item.tags?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 3 }}>
                {item.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.15)'
                          : 'rgba(0,0,0,0.15)',
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Links */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              {item.instanceUrl && (
                <Button
                  variant="contained"
                  startIcon={<LaunchIcon />}
                  href={item.instanceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    background: brand.gradients.button,
                    color: brand.buttonTextColor,
                    fontWeight: 600,
                    '&:hover': {
                      background: brand.gradients.buttonHover,
                    },
                  }}
                >
                  Live Demo
                </Button>
              )}
              {item.repositoryUrl && (
                <Button
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  href={item.repositoryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 600,
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.2)'
                        : 'rgba(0,0,0,0.2)',
                    '&:hover': {
                      borderColor: brand.colors.fire,
                      color: brand.colors.fire,
                    },
                  }}
                >
                  Repository
                </Button>
              )}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                pt: 2,
                borderTop: '1px solid',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.08)',
              }}
            >
              {isOwner && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    component={RouterLink}
                    to={`/gallery/${item._id}/edit`}
                    sx={{ fontWeight: 600 }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    disabled={deleting}
                    sx={{ fontWeight: 600 }}
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </>
              )}
              {isAuthenticated && !isOwner && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<FlagIcon />}
                  onClick={() => setReportOpen(true)}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    '&:hover': { color: 'error.main' },
                  }}
                >
                  Report
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Report dialog */}
      {item && (
        <ReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          itemId={item._id}
        />
      )}
    </Container>
  );
};

export default GalleryItemDetail;
